import { useCallback, useLayoutEffect, useRef, useState } from 'react'

import type { ConversationPosition } from './conversation-position'
import {
  captureConversationPosition,
  restoreConversationPosition,
} from './conversation-position'

/** Follow layout changes until the reader scrolls; hold their message when prepending history. */
export function useConversationScroll(
  initialPosition: ConversationPosition | undefined,
  messages: unknown,
  streamingContent: string | null,
  toolCount: number,
  resetKey?: string,
) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null)
  const viewportRef = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node
    setViewport(node)
  }, [])
  const pending = useRef(initialPosition)
  const pinned = useRef(true)

  // A portal can attach its viewport after the parent's effects have run.
  // Reset on that actual mount (including reopen), and on conversation changes.
  useLayoutEffect(() => {
    pending.current = initialPosition
    pinned.current = !initialPosition || initialPosition.atBottom
  }, [viewport, resetKey, initialPosition])

  useLayoutEffect(() => {
    if (!viewport) return
    let frame: number | undefined
    let intentTimer: ReturnType<typeof setTimeout> | undefined
    let userScrolling = false
    let pointerDown = false
    const restore = () => {
      if (pending.current)
        restoreConversationPosition(viewport, pending.current)
      else if (pinned.current) viewport.scrollTop = viewport.scrollHeight
    }
    const schedule = () => {
      if (frame !== undefined) return
      frame = requestAnimationFrame(() => {
        frame = undefined
        restore()
      })
    }
    const finishIntent = () => {
      clearTimeout(intentTimer)
      intentTimer = setTimeout(() => {
        userScrolling = false
      }, 200)
    }
    const beginIntent = () => {
      pending.current = undefined
      userScrolling = true
      finishIntent()
    }
    const handleScroll = () => {
      // Content growth, scroll anchoring and popover resizing also emit scroll
      // events. Only the reader's gestures may turn off following the bottom.
      if (userScrolling || pointerDown) {
        pinned.current =
          viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <=
          24
        finishIntent()
      } else schedule()
    }
    const handleWheel = (event: WheelEvent) => {
      if (!event.deltaY) return
      beginIntent()
      if (event.deltaY < 0) pinned.current = false
    }
    const handleKey = (event: KeyboardEvent) => {
      if (
        ![
          'ArrowUp',
          'ArrowDown',
          'PageUp',
          'PageDown',
          'Home',
          'End',
          ' ',
        ].includes(event.key)
      )
        return
      beginIntent()
      if (['ArrowUp', 'PageUp', 'Home'].includes(event.key))
        pinned.current = false
    }
    const handlePointerDown = () => {
      pointerDown = true
      beginIntent()
    }
    const handlePointerUp = () => {
      pointerDown = false
      finishIntent()
    }
    restore()
    schedule()
    const observer = new ResizeObserver(schedule)
    observer.observe(viewport)
    for (const child of viewport.children) observer.observe(child)
    // Radix's scrollbar is beside the viewport, so listen on its root too.
    const pointerTarget = viewport.hasAttribute(
      'data-radix-scroll-area-viewport',
    )
      ? (viewport.parentElement ?? viewport)
      : viewport
    viewport.addEventListener('scroll', handleScroll, { passive: true })
    viewport.addEventListener('wheel', handleWheel, { passive: true })
    viewport.addEventListener('keydown', handleKey)
    pointerTarget.addEventListener('pointerdown', handlePointerDown)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)
    return () => {
      observer.disconnect()
      if (frame !== undefined) cancelAnimationFrame(frame)
      clearTimeout(intentTimer)
      viewport.removeEventListener('scroll', handleScroll)
      viewport.removeEventListener('wheel', handleWheel)
      viewport.removeEventListener('keydown', handleKey)
      pointerTarget.removeEventListener('pointerdown', handlePointerDown)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [viewport, messages, streamingContent, toolCount, resetKey])

  const preservePosition = useCallback(() => {
    pending.current = captureConversationPosition(scrollRef.current, false)
    pinned.current = false
  }, [])
  const followLatest = useCallback(() => {
    pending.current = undefined
    pinned.current = true
  }, [])
  return { scrollRef, viewportRef, preservePosition, followLatest }
}
